import CategoryDisplay from '@components/Resources/Category/CategoryDisplay';
import CategoryLink from '@components/Resources/Category/CategoryLink';
import SubcategoryNavigation from '@components/Resources/Category/SubcategoryNavigation';

import {
  ArticleCategoriesType,
  ArticleCategoriesNodeType,
} from '@nextTypes/guides';

type Props = {
  allCategories: ArticleCategoriesType;
  categorySlug?: string;
};

const CategoryNavigation = ({
  allCategories,
  categorySlug,
}: Props): JSX.Element | null => {
  let selectedCategorySlug: string | undefined,
    parentCategorySlug: string | undefined,
    subcategoryEdges: ArticleCategoriesNodeType[] = [];

  if (categorySlug === 'all') {
    selectedCategorySlug = 'all';
  } else if (categorySlug) {
    // A category has been selected, see if it's a top-level category or subcategory

    const categoryEdge = allCategories.edges.find(
      edge => edge.node.slug === categorySlug,
    );

    // NB we are guaranteed at this point that the category slug will be somewhere in the category list
    // (otherwise the surrounding category page returns 404)
    if (!categoryEdge) {
      return null;
    }

    selectedCategorySlug = categorySlug;

    parentCategorySlug =
      categoryEdge.node.parent && categoryEdge.node.parent.slug;

    // Find other subcategories for the selected subcategory/category (if any)
    subcategoryEdges = allCategories.edges.filter(
      edge =>
        edge.node.parent &&
        edge.node.parent.slug ===
          (categoryEdge.node.parent
            ? categoryEdge.node.parent.slug
            : selectedCategorySlug),
    );
  }

  let keyIndex = 0;

  const topLevelCategoryEdges = allCategories.edges.filter(
    categoryEdge =>
      !categoryEdge.node.parent &&
      categoryEdge.node.name !== 'Featured' &&
      categoryEdge.node.name !== 'Uncategorised' &&
      categoryEdge.node.name !== 'Stories' &&
      categoryEdge.node.name !== 'Local',
  );

  const categoryLinks = [
    <CategoryLink
      link={{
        href: '/guides/category/all',
        title: 'All',
        isSelected: selectedCategorySlug === 'all',
      }}
      key={keyIndex++}
    />,
  ];

  topLevelCategoryEdges.forEach(categoryEdge => {
    categoryLinks.push(
      <CategoryLink
        link={{
          href:
            categoryEdge.node.name === 'Blog'
              ? '/blog'
              : `/guides/category/${categoryEdge.node.slug}`,
          title: categoryEdge.node.name,
          isSelected: selectedCategorySlug === categoryEdge.node.slug,
          isParent: parentCategorySlug === categoryEdge.node.slug,
        }}
        key={keyIndex++}
      />,
    );
  });

  let subcategoryLinkDisplay;
  if (subcategoryEdges.length > 0) {
    const subcategoryLinks = subcategoryEdges.map(edge => ({
      href: `${
        edge.node.parent?.name === 'Blog'
          ? `/blog/category`
          : `/guides/category/${edge.node.parent?.slug || ''}`
      }/${edge.node.slug}`,
      title: edge.node.name,
      isSelected: selectedCategorySlug === edge.node.slug,
      isSubcategory: true,
    }));

    subcategoryLinkDisplay = (
      <SubcategoryNavigation links={subcategoryLinks} key={keyIndex++} />
    );
  }

  return (
    <CategoryDisplay
      categoryLinks={categoryLinks}
      subcategoryLinkDisplay={subcategoryLinkDisplay}
    />
  );
};

export default CategoryNavigation;
